import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import {
  markCmsOtpRequested,
  useCmsAuth,
  wasCmsOtpRequested,
} from "../context/CmsAuthContext";

export default function CmsOtpPage() {
  const {
    user,
    isCmsStaff,
    otpVerified,
    mustSetPassword,
    loading,
    requestOtp,
    verifyOtp,
    logout,
  } = useCmsAuth();
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState(
    "Al validar usuario y contraseña enviamos el código a su correo. Puede reenviar si no llega."
  );
  const [error, setError] = useState("");
  const sendInFlight = useRef(false);
  const autoTried = useRef(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("cmsIdleLogout") === "1") {
        sessionStorage.removeItem("cmsIdleLogout");
        const label = sessionStorage.getItem("cmsIdleLogoutLabel") || "10 min";
        sessionStorage.removeItem("cmsIdleLogoutLabel");
        sessionStorage.removeItem("cmsIdleLogoutMins");
        setMessage(
          `Sesión cerrada por inactividad (${label} sin uso). Inicie sesión de nuevo. Si aún tiene el código OTP (válido 2 h), úselo sin esperar un correo nuevo.`
        );
      }
      const autoErr = sessionStorage.getItem("cmsOtpAutoSendErr");
      if (autoErr) {
        sessionStorage.removeItem("cmsOtpAutoSendErr");
        setError(autoErr);
        setMessage("No se pudo enviar el OTP al entrar. Pulse «Reenviar código».");
      } else if (sessionStorage.getItem("cmsOtpAutoSendOk") === "1") {
        sessionStorage.removeItem("cmsOtpAutoSendOk");
        setMessage("Enviamos un código de 6 dígitos a su correo (vigente 2 horas).");
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Si llega a OTP sin envío previo (p. ej. recarga), dispara uno solo una vez.
  useEffect(() => {
    if (loading || !user || !isCmsStaff || otpVerified || autoTried.current) return;
    if (wasCmsOtpRequested(user.uid)) {
      autoTried.current = true;
      return;
    }
    autoTried.current = true;
    void (async () => {
      if (sendInFlight.current) return;
      sendInFlight.current = true;
      setSending(true);
      setError("");
      try {
        const data = await requestOtp();
        markCmsOtpRequested(user.uid);
        if (data?.reused) {
          const mins = Math.max(1, Math.round((data.expiresInSec || 0) / 60));
          setMessage(
            `Ya hay un código vigente (~${mins} min). Revise correo/spam; puede reenviar tras ~60 s.`
          );
        } else {
          setMessage("Enviamos un código de 6 dígitos a su correo (vigente 2 horas).");
        }
      } catch (err) {
        setError(err?.message || "No se pudo enviar el código.");
      } finally {
        sendInFlight.current = false;
        setSending(false);
      }
    })();
  }, [loading, user, isCmsStaff, otpVerified, requestOtp]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafcfb]">
        <Loader2 className="animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!user || !isCmsStaff) {
    return <Navigate to="/admin/login" replace />;
  }

  if (otpVerified) {
    if (mustSetPassword) {
      return <Navigate to="/admin/set-password" replace />;
    }
    return <Navigate to="/admin" replace />;
  }

  const onSendCode = async () => {
    if (sendInFlight.current || sending) return;
    sendInFlight.current = true;
    setSending(true);
    setError("");
    try {
      const data = await requestOtp();
      markCmsOtpRequested(user.uid);
      if (data?.reused) {
        const mins = Math.max(1, Math.round((data.expiresInSec || 0) / 60));
        setMessage(
          `Espere ~60 s entre envíos. Ya hay un código reciente (~${mins} min de vigencia). Revise bandeja y spam.`
        );
      } else {
        setMessage("Enviamos un código de 6 dígitos a su correo (vigente 2 horas).");
      }
    } catch (err) {
      setError(err?.message || "No se pudo enviar el código.");
    } finally {
      sendInFlight.current = false;
      setSending(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError("");
    try {
      await verifyOtp(code.trim());
    } catch (err) {
      setError(err?.message || "Código incorrecto.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050e08] flex items-center justify-center px-6 py-16">
      <form
        onSubmit={onVerify}
        className="w-full max-w-md bg-white rounded-[32px] p-8 shadow-2xl space-y-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
            <ShieldCheck size={22} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black text-brand-dark">
              Verificación OTP
            </h1>
            <p className="text-sm text-brand-dark/60">
              Digite el token enviado a {user.email}
            </p>
          </div>
        </div>

        {message && (
          <p className="text-sm text-brand-primary bg-brand-soft/40 rounded-2xl p-4">{message}</p>
        )}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-4">
            {error}
          </p>
        )}

        <input
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full text-center text-3xl tracking-[0.4em] font-black rounded-2xl border border-brand-soft bg-[#f8faf9] py-4 outline-none focus:ring-2 focus:ring-brand-primary"
          placeholder="000000"
        />

        <button
          type="submit"
          disabled={verifying || code.length !== 6}
          className="w-full bg-brand-primary text-white py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-brand-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {verifying ? <Loader2 className="animate-spin" size={16} /> : null}
          Verificar e ingresar
        </button>

        <div className="flex items-center justify-between text-xs gap-3">
          <button
            type="button"
            onClick={onSendCode}
            disabled={sending}
            className="font-bold text-brand-primary hover:underline disabled:opacity-50"
          >
            {sending ? "Enviando…" : "Reenviar código"}
          </button>
          <button
            type="button"
            onClick={logout}
            className="font-bold text-brand-dark/50 hover:text-brand-dark"
          >
            Cerrar sesión
          </button>
        </div>
      </form>
    </div>
  );
}
